// FILE: src/utils/mfa.js
// Lightweight client-side helpers for MFA enrollment/verification.
// NOTE: In a production app, most of this logic should live on a trusted backend.
import CryptoJS from "crypto-js";
import {
  arrayUnion,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db, auth } from "../firebase";

const SESSION_KEY_PREFIX = "mfaSession:";
const TRUST_KEY_PREFIX = "mfaTrust:";
const BASE32_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
const webCrypto = typeof crypto !== "undefined" ? crypto : null;

function base32Encode(bytes) {
  let bits = "";
  let output = "";
  bytes.forEach((b) => {
    bits += b.toString(2).padStart(8, "0");
    while (bits.length >= 5) {
      const chunk = bits.slice(0, 5);
      bits = bits.slice(5);
      output += BASE32_ALPHABET[parseInt(chunk, 2)];
    }
  });
  if (bits.length) {
    output += BASE32_ALPHABET[parseInt(bits.padEnd(5, "0"), 2)];
  }
  return output;
}

function base32Decode(str) {
  let bits = "";
  const bytes = [];
  const clean = str.replace(/=+$/g, "").toUpperCase();
  [...clean].forEach((ch) => {
    const idx = BASE32_ALPHABET.indexOf(ch);
    if (idx < 0) return;
    bits += idx.toString(2).padStart(5, "0");
    while (bits.length >= 8) {
      bytes.push(parseInt(bits.slice(0, 8), 2));
      bits = bits.slice(8);
    }
  });
  return Uint8Array.from(bytes);
}

function hashValue(value) {
  return CryptoJS.SHA256(value).toString(CryptoJS.enc.Hex);
}

function encryptSecret(secret, uid) {
  return CryptoJS.AES.encrypt(secret, uid || "mfa").toString();
}

function decryptSecret(ciphertext, uid) {
  if (!ciphertext) return "";
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, uid || "mfa");
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return "";
  }
}

function generateBackupCodes(count = 8) {
  const plain = [];
  const hashes = [];
  for (let i = 0; i < count; i += 1) {
    const buf = new Uint8Array(8);
    const rand = webCrypto || (typeof window !== "undefined" ? window.crypto : null);
    (rand || { getRandomValues: (arr) => arr.fill(0) }).getRandomValues(buf);
    const raw = Array.from(buf).map((b) => b.toString(16).padStart(2, "0")).join("").slice(0, 10).toUpperCase();
    const formatted = `${raw.slice(0, 4)}-${raw.slice(4, 8)}-${raw.slice(8)}`;
    plain.push(formatted);
    hashes.push(hashValue(formatted));
  }
  return { plain, hashes };
}

function generateTotpSecret() {
  const bytes = new Uint8Array(20);
  const rand = webCrypto || (typeof window !== "undefined" ? window.crypto : null);
  (rand || { getRandomValues: (arr) => arr.fill(0) }).getRandomValues(bytes);
  return base32Encode(bytes);
}

async function generateQrForSecret({ issuer = "Lastwish Box", label, secret }) {
  const otpauth = `otpauth://totp/${encodeURIComponent(label)}?secret=${secret}&issuer=${encodeURIComponent(issuer)}`;
  const qrDataUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
    otpauth
  )}`;
  return { otpauth, qrDataUrl };
}

async function loadMfaProfile(uid) {
  if (!uid) return null;
  const ref = doc(db, "users", uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const data = snap.data() || {};
  const mfa = data.mfa || {};
  const totp = mfa.totp || {};
  const recovery = mfa.recovery || {};
  const enabled = mfa.enabled ?? totp.enabled ?? false;
  const secretEnc = mfa.secretEnc || totp.secretEnc || "";
  const secretBase32 = mfa.secretBase32 || totp.base32 || "";
  const backupCodeHashes = Array.isArray(mfa.backupCodeHashes)
    ? mfa.backupCodeHashes
    : Array.isArray(recovery.hashes)
      ? recovery.hashes
      : [];
  const trustedDevices = Array.isArray(mfa.trustedDevices)
    ? mfa.trustedDevices
    : Array.isArray(totp.trustedDevices)
      ? totp.trustedDevices
      : [];
  return {
    enabled: !!enabled,
    method: (mfa.method || (mfa.email && mfa.email.enabled ? "email" : "totp")).toLowerCase(),
    emailEnabled: !!(mfa.email && mfa.email.enabled),
    secretEnc,
    secretBase32,
    backupCodeHashes,
    trustedDevices,
  };
}

async function saveMfaProfile(uid, mfaPatch) {
  if (!uid) return;
  const ref = doc(db, "users", uid);
  const existingSnap = await getDoc(ref);
  const current = existingSnap.exists() ? existingSnap.data()?.mfa || {} : {};
  await setDoc(
    ref,
    { mfa: { ...current, ...mfaPatch, updatedAt: serverTimestamp() } },
    { merge: true }
  );
}

async function updateTrustedDevice(uid, trustedDevice) {
  const ref = doc(db, "users", uid);
  await updateDoc(ref, {
    "mfa.trustedDevices": arrayUnion(trustedDevice),
    "mfa.updatedAt": serverTimestamp(),
  });
}


function getCurrentAuthTimeSec() {
  try {
    const token =
      auth?.currentUser?.stsTokenManager?.accessToken ||
      auth?.currentUser?.accessToken ||
      "";
    if (!token || !token.includes(".")) return null;
    const payloadB64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const jsonStr = decodeURIComponent(
      atob(payloadB64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const payload = JSON.parse(jsonStr);
    return typeof payload.auth_time === "number" ? payload.auth_time : null;
  } catch {
    return null;
  }
}


function setSessionBypass(uid, ttlMinutes = 10) {
  const exp = Math.floor(Date.now() / 1000) + ttlMinutes * 60;
  const authTime = getCurrentAuthTimeSec();
  try {
    sessionStorage.setItem(
      `${SESSION_KEY_PREFIX}${uid}`,
      JSON.stringify({ exp, authTime })
    );
  } catch {}
}

function clearSessionBypass(uid) {
  try {
    if (uid) {
      sessionStorage.removeItem(`${SESSION_KEY_PREFIX}${uid}`);
      return;
    }
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith(SESSION_KEY_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    });
  } catch {}
}



function hasSessionBypass(uid) {
  try {
    const raw = sessionStorage.getItem(`${SESSION_KEY_PREFIX}${uid}`);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    const now = Math.floor(Date.now() / 1000);
    if (!parsed.exp || parsed.exp <= now + 15) return false;

    // Bind bypass to the current Firebase sign-in.
    // If the user logs out/in again, auth_time changes and old bypass is rejected.
    const currentAuthTime = getCurrentAuthTimeSec();
    if (!parsed.authTime || !currentAuthTime) return false;
    if (parsed.authTime !== currentAuthTime) return false;

    return true;
  } catch {
    return false;
  }
}

function getLocalTrustedToken(uid) {
  try {
    const raw = localStorage.getItem(`${TRUST_KEY_PREFIX}${uid}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed.token || !parsed.exp) return null;
    if (parsed.exp <= Math.floor(Date.now() / 1000)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function cacheLocalTrustedToken(uid, token, days = 30) {
  const exp = Math.floor(Date.now() / 1000) + days * 24 * 60 * 60;
  try {
    localStorage.setItem(
      `${TRUST_KEY_PREFIX}${uid}`,
      JSON.stringify({ token, exp })
    );
  } catch {}
}

function isTrustedDevice(profile, uid) {
  const local = getLocalTrustedToken(uid);
  if (!local || !profile) return false;
  const hashed = hashValue(local.token);
  return profile.trustedDevices?.some((d) => d.tokenHash === hashed && d.expiresAt > Date.now());
}

function hotp(secret, counter) {
  const key = base32Decode(secret);
  const counterBytes = new Uint8Array(8);
  const view = new DataView(counterBytes.buffer);
  view.setUint32(4, counter, false);
  const hmac = CryptoJS.HmacSHA1(
    CryptoJS.lib.WordArray.create(counterBytes),
    CryptoJS.lib.WordArray.create(key)
  ).toString(CryptoJS.enc.Hex);
  const offset = parseInt(hmac.slice(-1), 16) & 0xf;
  const bin = (parseInt(hmac.substr(offset * 2, 8), 16) & 0x7fffffff) % 1000000;
  return bin;
}

function verifyTotp(code, secret) {
  if (!code || !secret) return false;
  const clean = code.replace(/\s+/g, "");
  const numCode = parseInt(clean, 10);
  if (Number.isNaN(numCode)) return false;
  const counter = Math.floor(Date.now() / 1000 / 30);
  const window = [counter - 1, counter, counter + 1];
  return window.some((c) => hotp(secret, c) === numCode);
}

function describeDevice() {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "unknown";
  const plat = typeof navigator !== "undefined" ? navigator.platform : "";
  return `${plat || ""} ${ua}`.trim().slice(0, 200);
}



async function sendEmailMfaCode() {
  const u = auth.currentUser;
  if (!u) throw new Error("Not authenticated");
  const token = await u.getIdToken();
  const res = await fetch("/api/mfa/sendEmailCode", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.ok) {
    throw new Error(data.error || "Failed to send email code");
  }
  return data;
}

async function verifyEmailMfaCode(code) {
  const u = auth.currentUser;
  if (!u) throw new Error("Not authenticated");
  const token = await u.getIdToken();
  const res = await fetch("/api/mfa/verifyEmailCode", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ code }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.ok) {
    throw new Error(data.error || "Invalid email code");
  }
  return data;
}

export {
  cacheLocalTrustedToken,
  clearSessionBypass,
  decryptSecret,
  describeDevice,
  encryptSecret,
  generateBackupCodes,
  generateQrForSecret,
  generateTotpSecret,
  getLocalTrustedToken,
  hasSessionBypass,
  isTrustedDevice,
  loadMfaProfile,
  saveMfaProfile,
  setSessionBypass,
  updateTrustedDevice,
  verifyTotp,
  sendEmailMfaCode,
  verifyEmailMfaCode,
  getCurrentAuthTimeSec,
};
